import React, { useEffect, useState, useRef, useMemo, forwardRef } from 'react';
import { useDispatch } from "react-redux";
import { useNavigate } from 'react-router-dom';
import { listDevice, printHistory } from '../_actions/user_action';

import {
    Box,
    Button,
    Stack,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Pagination,
    TextField
} from '@mui/material';

import DatePicker from "react-datepicker";
import 'react-datepicker/dist/react-datepicker.css';

import { CSVLink } from 'react-csv';
import moment from 'moment';
import 'moment/locale/ko';
import { getUserAccount, getIsLogin } from '../utils/session';

export default function MobileHomePrintList() {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // state, hooks 동일…
    const [PartNo, setPartNo] = useState('');
    const [deviceListInfo, setDeviceListInfo] = useState([]);
    const [startDate, setStartDate] = useState(new Date());
    const [endDate, setEndDate] = useState(new Date());
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [sortDirection, setSortDirection] = useState('desc');
    const arrayPostsRef = useRef([]);
    
    const POST_PER_PAGE = 10;
    const LAST_PAGE = Math.ceil(posts.length / POST_PER_PAGE);

    useEffect(() => {
        if (!getIsLogin()) {
            navigate('/mobile');
            return;
        }
        const user = getUserAccount() || {};
        dispatch(listDevice({ id: user.email })).then(res => {
            if (res.payload.listSuccess) {
                setDeviceListInfo(res.payload.deviceList.filter(d => d.sensorId));
            }
        });
    }, []);

    const handleSearch = async () => {
        if (!PartNo) return alert("장비를 선택하세요.");
        if (startDate > endDate) return alert("시작일이 종료일보다 이후일 수 없습니다.");
        setLoading(true);
        setPosts([]);
        const devices = PartNo === 'ALL'
        ? deviceListInfo.map(d => d.sensorId)
        : [deviceListInfo.find(d => d.sensorNumber===PartNo)?.sensorId].filter(Boolean);

        try {
        const res = await dispatch(printHistory({
            devices,
            startDate: moment(startDate).format("YYYY-MM-DD"),
            endDate: moment(endDate).format("YYYY-MM-DD")
        }));
        if (res.payload.findSuccess) {
            const data = res.payload.sensorList.map((it, i) => ({
                pID: i,
                pTime: it.KSTTime.replace("T"," [").replace("+09:00","]"),
                pTimestamp: it.Timestamp,
                pDevice: deviceListInfo.find(d=>d.sensorId===it.DeviceId)?.sensorNumber||it.DeviceId,
                pNH3: it.NH3,
                pH2S: it.H2S,
                pTemperature: it.Temperature,
                pHumidity: it.Humidity,
                pRssi: it.Rssi,
                pSnr: it.Snr
            }));
            arrayPostsRef.current = data;
            setPosts(data);
            setPage(1);
        }
        } catch {
            alert('이력 조회 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const toggleSort = () => setSortDirection(d=>d==='asc'?'desc':'asc');
    const sorted = useMemo(() => [...posts].sort((a,b)=>
        sortDirection==='asc'? a.pTimestamp-b.pTimestamp : b.pTimestamp-a.pTimestamp
    ),[posts,sortDirection]);
    const paged = sorted.slice((page-1)*POST_PER_PAGE, page*POST_PER_PAGE);

    // DatePicker용 custom input
    const PickerInput = forwardRef(({ value, onClick }, ref) => (
        <TextField
            ref={ref}
            onClick={onClick}
            value={value}
            variant="outlined"
            size="small"
            sx={{
                width: { xs: 110, sm: 140, md: 170 },
                height: 40,
                '& .MuiInputBase-input': {
                    px: 1.5,
                    py: 0.5,
                    fontSize: 16,
                    lineHeight: '1.6',
                },
            }}
        />
    ));
    
    return (
        <Box sx={{ width:'100%', p:2, boxSizing:'border-box' }}>
        {/* 검색 폼: xs=column, sm=row */}
        <Stack
            direction={{ xs:'column', sm:'row' }}
            spacing={1}
            mb={2}
        >
            {/* 장비 선택 */}
            <FormControl fullWidth sx={{ minWidth:{ xs:'100%', sm:200 } }}>
            <InputLabel id="device-select-label">장비 선택</InputLabel>
            <Select
                labelId="device-select-label"
                value={PartNo}
                label="PartNo"
                onChange={e=>{ setPartNo(e.target.value); setPage(1) }}
                MenuProps={{
                    disableScrollLock: true,
                    PopoverProps: {
                        disableScrollLock: true,
                    }
                }}
                sx={{
                    height: 50,
                }}
            >
                <MenuItem value="ALL">전체</MenuItem>
                {deviceListInfo.map(d=>(
                <MenuItem key={d.sensorNumber} value={d.sensorNumber}>
                    {d.sensorNumber}
                </MenuItem>
                ))}
            </Select>
            </FormControl>

            {/* 기간 검색 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DatePicker
                selected={startDate}
                onChange={setStartDate}
                dateFormat="yyyy-MM-dd"
                customInput={<PickerInput />}
            />
            </Box>
            <Box component="span" sx={{ fontSize:14, lineHeight:'10px' }}>~</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DatePicker
                selected={endDate}
                onChange={setEndDate}
                minDate={startDate}
                dateFormat="yyyy-MM-dd"
                customInput={<PickerInput />}
            />
            </Box>

            {/* 검색 버튼 */}
            <Button
                variant="contained"
                onClick={handleSearch}
                fullWidth
                sx={{
                    backgroundColor:'#4e82eb',
                    color:'white',
                    py:1,
                    fontSize: 16
                }}
            >
                검 색
            </Button>
        </Stack>

        {/* 결과 테이블: 스크롤 허용 */}
        <Box sx={{ width:'100%', overflowX:'auto' }}>
            {/* 헤더 */}
            <Box
                component="ul"
                sx={{
                    display:'flex',
                    m:0,
                    p:0,
                    listStyle:'none',
                    borderBottom:'1px solid #ddd'
                }}
            >
            {['측정시간','장비 No.','암모니아','황화수소','온도','습도','신호품질','신호세기'].map((lbl,i)=>(
                <Box
                    key={i}
                    component="li"
                    onClick={i===0 ? toggleSort : undefined}
                    sx={{
                        flex:1,
                        minWidth:100,
                        textAlign:'center',
                        py:1,
                        cursor:i===0?'pointer':'default'
                    }}
                >
                {i===0? `${lbl} ${sortDirection==='asc'?'▲':'▼'}`: lbl}
                </Box>
            ))}
            </Box>

            {/* 데이터 */}
            {loading
            ? <Box sx={{ p:2, textAlign:'center' }}>Loading...</Box>
            : paged.length > 0
                ? paged.map(el=>(
                    <Box
                        component="ul"
                        key={el.pID}
                        sx={{
                            display:'flex',
                            m:0,
                            p:0,
                            listStyle:'none',
                            borderBottom:'1px solid #f0f0f0'
                        }}
                    >
                    {['pTime','pDevice','pNH3','pH2S','pTemperature','pHumidity','pRssi','pSnr'].map((k,j)=>(
                        <Box
                            component="li"
                            key={j}
                            sx={{
                                flex:1,
                                minWidth:100,
                                textAlign:'center',
                                py:1,
                                fontSize:14
                            }}
                        >
                            {
                            k === 'pTime' ? el.pTime :
                            k === 'pDevice' ? el.pDevice :
                            k === 'pRssi' ? parseInt(el[k] || 0) :
                            k === 'pSnr' ? parseFloat(el[k] || 0).toFixed(1) :
                            parseFloat(el[k] || 0).toFixed(k === 'pHumidity' ? 3 : 2)
                            }
                        </Box>
                    ))}
                    </Box>
                ))
                : <Box sx={{ p:2, textAlign:'center', color:'#999' }}>
                    검색된 측정 이력이 없습니다.
                </Box>
            }
        </Box>

        {/* CSV 저장 + 페이징 */}
        <Stack
            direction="column"
            spacing={1}
            alignItems="center"
            mt={2}
            >
            {/* 페이지네이션 */}
            {LAST_PAGE > 1 && (
            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    overflowX: 'auto',          // 페이지가 많아도 스크롤
                    py: 1,
                }}
                >
                <Pagination
                    count={LAST_PAGE}
                    page={page}
                    onChange={(_, v) => setPage(v)}
                    shape="rounded"
                    boundaryCount={1}
                    siblingCount={0}
                    sx={{
                        '& .MuiPagination-ul': {
                            flexWrap: 'nowrap',
                            whiteSpace: 'nowrap',
                        }
                    }}
                />
            </Box>
            )}

            {/* CSV 저장 버튼 */}
            <CSVLink
                headers={[
                { label: "측정시간", key: "pTime" },
                { label: "장비 Part No.", key: "pDevice" },
                { label: "암모니아 ppm", key: "pNH3" },
                { label: "황화수소 ppm", key: "pH2S" },
                { label: "온도 ℃", key: "pTemperature" },
                { label: "습도 %", key: "pHumidity" }
                ]}
                data={arrayPostsRef.current}
                filename={`측정이력_${moment().format('YYYYMMDD_HHmmss')}`}
            >
                <Button
                    variant="contained"
                    disabled={!posts.length}
                    sx={{
                        backgroundColor: '#4e82eb',
                        color: 'white',
                        px: 3,
                        mt: 1  // 페이지네이션과 약간의 간격
                    }}
                >
                    측정이력 저장
                </Button>
            </CSVLink>
            </Stack>
        </Box>
    );
}
